import React, { useState, useEffect, useContext, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import GameControls from '../components/GameControls';
import GreekTileGrid from '../components/GreekTileGrid';
import { GameContext } from '../context/GameContext';

type RunningLionProps = {
    isRunning: boolean | null
}

// FallingImage.tsx
interface FallingImageProps {
    key: number;
    src: string;
    column: number;
    spacing: number; // Add this line
    delay: number;
    isRunning: boolean;
    onClick: () => void;
}

interface LetterImageMapping {
    [key: string]: string[];
}

//clickable version
interface FallingImages {
    src: string
    delay: number
    column: number
    key: number
    clicked: boolean
    isCorrect: boolean

}

// Add new interface for wrong answer animation
interface WrongAnswerAnimationProps {
    isVisible: boolean;
    onAnimationComplete: () => void;
}


// Add new component for wrong answer animation
const WrongAnswerAnimation: React.FC<WrongAnswerAnimationProps> = ({ isVisible, onAnimationComplete }) => {
    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isVisible) {
            // Duration should match your video length
            timer = setTimeout(() => {
                onAnimationComplete();
            }, 2000); // Adjust this to match your video duration
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isVisible, onAnimationComplete]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
            <video
                autoPlay
                muted
                className="w-full h-full object-cover"
                onEnded={onAnimationComplete}
                style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%' }}
            >
                <source src="/videos/ClawAnimation.mp4" type="video/mp4" />
            </video>
        </div>
    );
};

const RunningLion: React.FC<RunningLionProps> = ({ isRunning }) => {
    return (
        <div style={{
            overflow: 'hidden',
            width: '100vw',
            height: '800px',
            position: 'relative',
        }}>
            <motion.img
                src="/images/MovieLion.gif"
                alt="Running Lion"
                style={{
                    width: '650px',
                    height: '650px',
                    position: 'absolute',
                    bottom: '-150px',
                    objectFit: 'contain',
                    transform: 'scaleX(-1)',
                }}
                initial={{ x: '100vw' }}
                animate={isRunning ? { x: '-600px' } : { x: '100vw' }}
                transition={{
                    x: {
                        duration: isRunning ? 3 : 0,
                        repeat: 0,
                        ease: "linear",
                        type: "tween"
                    }
                }}
            />
        </div>
    );
};

const IMAGE_WIDTH = 175;       // Image width in pixels
const VIEWPORT_PADDING = 50;  // Reduced padding for better responsiveness
const FALL_DURATION = 4;      // Fall duration in seconds

const calculateColumns = () => {
    const viewportWidth = window.innerWidth;
    const usableWidth = viewportWidth - (VIEWPORT_PADDING * 2);
    const maxColumns = Math.floor(usableWidth / IMAGE_WIDTH) || 1;
    const spacing = (usableWidth - (maxColumns * IMAGE_WIDTH)) / (maxColumns - 1 || 1);
    return { maxColumns, spacing };
};


const FallingImage: React.FC<FallingImageProps> = ({ src, column, spacing, delay, onClick, isRunning }) => {

    return (
        <motion.div
            style={{
                width: `${IMAGE_WIDTH}px`,
                height: `${IMAGE_WIDTH}px`,
                position: 'absolute',
                top: -IMAGE_WIDTH,
                left: `${VIEWPORT_PADDING + (column * (IMAGE_WIDTH + spacing))}px`,
                overflow: 'hidden',
                cursor: 'pointer',
                zIndex: 20,
            }}
            initial={{ y: -IMAGE_WIDTH }}
            animate={{ y: window.innerHeight + IMAGE_WIDTH }}
            transition={{
                y: {
                    duration: FALL_DURATION,
                    repeat: 0,
                    ease: "linear",
                    delay: isRunning ? 0 : delay
                }
            }}
            onClick={onClick}
            whileHover={{ scale: 1.4 }} // Optional: add hover effect
            whileTap={{ scale: 0.95 }} // Optional: add click effect

        >
            <img
                src={src}
                alt="Falling Image"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none', // Prevent image from interfering with clicks

                }}
            />
        </motion.div>
    );
};


// Update the getRandomImagesForLetter function to track correct/incorrect images
const getRandomImagesForLetter = (letter: string, imageSources: string[], letterToImages: LetterImageMapping) => {
    const correctImage = letterToImages[letter][0];


    const wrongImagePool = imageSources.filter(img => !correctImage.includes(img));

    const randomWrongImages: string[] = [];
    while (randomWrongImages.length < 3) {
        const randomIndex = Math.floor(Math.random() * wrongImagePool.length);
        const selectedImage = wrongImagePool[randomIndex];
        if (!randomWrongImages.includes(selectedImage)) {
            randomWrongImages.push(selectedImage);
        }
    }

    // Create objects with isCorrect property
    const allImages = [
        { src: correctImage, isCorrect: true },
        ...randomWrongImages.map(src => ({ src, isCorrect: false }))
    ];

    // Shuffle the combined array
    return allImages.sort(() => Math.random() - 0.5);
};

const GameComponent = () => {
    const { score, setScore } = useContext(GameContext)!; // Use context
    const [isRunning, setIsRunning] = useState(false);
    const [isFalling, setIsFalling] = useState(false);
    const [fallingImages, setFallingImages] = useState<FallingImages[]>([]);
    const [showLetters, setShowLetters] = useState(true); // New state for letters visibility
    const [showWrongAnimation, setShowWrongAnimation] = useState(false);

    const [columns, setColumns] = useState<{ maxColumns: number; spacing: number }>({ maxColumns: 1, spacing: 0 });

    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(true); // Start as true for initial play state

    useEffect(() => {
        // Initialize audio when component mounts
        if (audioRef.current) {
            audioRef.current.play().catch(error => {
                console.log('Audio autoplay failed:', error);
                setIsPlaying(false); // Update state if autoplay fails
            });
        }
    }, []);

    const handleAudioToggle = () => {
        if (audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play();
                setIsPlaying(true);
            } else {
                audioRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    useEffect(() => {
        const updateColumns = () => {
            setColumns(calculateColumns());
        };

        updateColumns();
        window.addEventListener('resize', updateColumns);
        return () => window.removeEventListener('resize', updateColumns);
    }, []);


    const imageSources = [
        '/gameplay/animals.png',//
        '/gameplay/arrow.png',//
        '/gameplay/cave.png',//
        '/gameplay/club.png',//
        '/gameplay/dream.png',//
        '/gameplay/Eurystheus.png',//
        '/gameplay/fish.png',//
        '/gameplay/foreigner.png',//
        '/gameplay/giant.png',//
        '/gameplay/God.png',//
        '/gameplay/Hands.png',//
        '/gameplay/hercules.png',//
        '/gameplay/history.png',//
        '/gameplay/house.png',//
        '/gameplay/hunter.png',//
        '/gameplay/labor.png',//
        '/gameplay/lion.png',//
        '/gameplay/mountain.png',//
        '/gameplay/Mycenae.png',//
        '/gameplay/Nemea.png',//
        '/gameplay/Peloponnese.png',//
        '/gameplay/person.png',//
        '/gameplay/place.png',//
        '/gameplay/plant.png',//
        '/gameplay/skin.png',//
        '/gameplay/time.png',//
        '/gameplay/tree.png',//
        '/gameplay/urn.png',//
        '/gameplay/wolf.png',
        //king?


    ];//array so has an index

    const rowLength = 15;

    //time based approach

    // Optional fallback timeout
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (isFalling) {
            timeoutId = setTimeout(() => {
                setIsFalling(false);
                setShowLetters(true);
                setFallingImages([]);
            }, 6000);
        }
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [isFalling]);


    // Create the mapping object
    const letterToImages: LetterImageMapping = {
        'Α': ['/gameplay/person.png'],
        'Β': ['/gameplay/Eurystheus.png'],
        'Γ': ['/gameplay/giant.png'],
        'Δ': ['/gameplay/tree.png'],
        'Ε': ['/gameplay/Eurystheus.png'],
        'Ζ': ['/gameplay/animals.png'],
        'Η': ['/gameplay/hercules.png'],
        'Θ': ['/gameplay/God.png'],
        'Ι': ['/gameplay/history.png'],
        'Κ': ['/gameplay/hunter.png'],
        'Λ': ['/gameplay/lion.png'],
        'Μ': ['/gameplay/Mycenae.png'],
        'Ν': ['/gameplay/Nemea.png'],
        'Ξ': ['/gameplay/foreigner.png'],
        'Ο': ['/gameplay/dream.png'],
        'Π': ['/gameplay/Peloponnese.png'],
        'Ρ': ['/gameplay/club.png'],
        'Σ': ['/gameplay/cave.png'],
        'Τ': ['/gameplay/place.png'],
        'Υ': ['/gameplay/giant.png'],
        'Φ': ['/gameplay/plant.png'],
        'Χ': ['/gameplay/Hands.png'],
        'Ψ': ['/gameplay/fish.png'],
        'Ω': ['/gameplay/time.png']
    };

    // Update your handleImageClick function to trigger lion animation
    // Handle any image click sequence
    const handleImageClick = (key: number) => {
        const clickedImage = fallingImages.find(img => img.key === key);
        if (!clickedImage || clickedImage.clicked) return;

        // Immediately stop falling images and hide letters for any click
        setIsFalling(false);
        setShowLetters(false);
        setFallingImages([]);

        // Update clicked status
        setFallingImages(prev =>
            prev.map(img =>
                img.key === key ? { ...img, clicked: true } : img
            )
        );

        if (clickedImage.isCorrect) {
            // Correct answer sequence
            setIsRunning(true);

            setScore(prev => prev + 10); // Use context's setScore

            // Reset after lion animation
            setTimeout(() => {
                setIsRunning(false);
                setShowLetters(true);
            }, 3000); // Match this with lion animation duration
        } else {
            // Wrong answer sequence
            setShowWrongAnimation(true);

            setTimeout(() => {
                setScore(prev => prev - 10);  // Update score after wrong animation
            }, 500);

            // Reset after wrong animation
            setTimeout(() => {
                setShowWrongAnimation(false);
                setShowLetters(true);
            }, 1000); // Adjust to match your video duration
        }
    };

    // // Calculate available columns
    // const usableWidth = window.innerWidth - (VIEWPORT_PADDING * 2);
    // // console.log("usableWidth:", usableWidth);

    // const remainingSpace = usableWidth - IMAGE_WIDTH;
    // // console.log("remainingSpace:", remainingSpace);

    // const additionalCombos = Math.floor(remainingSpace / (COLUMN_SPACING + IMAGE_WIDTH));
    // // console.log("additionalCombos:", additionalCombos, (COLUMN_SPACING + IMAGE_WIDTH));

    // const maxColumns = additionalCombos + 1;
    // // console.log("maxColumns:", maxColumns);

    // const DELAY_BETWEEN_IMAGES = 0.5;   // Longer delay between falls
    // // console.log("DELAY_BETWEEN_IMAGES:", DELAY_BETWEEN_IMAGES);
    // const availableColumns: number[] = []
    // for (let i = 0; i < maxColumns; i++) {
    //     availableColumns[i] = i
    // }

    // Utility function to shuffle an array using Fisher-Yates algorithm
    const shuffleArray = (array: number[]): number[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Function to handle starting a new fall animation
    const handleStartFalling = (letter: string) => {
        const selectedImages = getRandomImagesForLetter(letter, imageSources, letterToImages);

        // Shuffle columns to randomize their assignment
        const shuffledColumns = shuffleArray([...Array(columns.maxColumns).keys()]);

        const newFallingImages = selectedImages.map((img, index) => {
            // Assign columns in a randomized order
            const column = shuffledColumns[index % shuffledColumns.length];

            // Generate a random delay between 0 and 1.5 seconds for staggered fall
            const randomDelay = Math.random() * 1.5;

            return {
                src: img.src,
                delay: randomDelay, // Use randomized delay
                column: column,     // Use randomized column
                spacing: columns.spacing,
                key: Date.now() + index,
                clicked: false,
                isCorrect: img.isCorrect
            };
        });
        setFallingImages(newFallingImages);
        setIsFalling(true);
    };


    return (
        <>
            {/* Add audio element if changes need to be made a global state management system would be needed*/}
            <audio ref={audioRef} src="/audio/Apollon-audio.mp3" loop />
            <GameControls isPlaying={isPlaying} onPlayPause={handleAudioToggle} />


            {/* Add wrong answer animation */}
            <WrongAnswerAnimation
                isVisible={showWrongAnimation}
                onAnimationComplete={() => setShowWrongAnimation(false)}
            />

            <div className='absolute top-10 right-4 z-40 flex flex-col gap-2'>

                <motion.div
                    className='bg-slate-800/90 p-2 rounded flex items-center gap-2'
                    key={`score-${score}`}
                    animate={{
                        rotate: [0, -15, 15, -15, 0],  // Gentle rotation for vibration effect
                    }}
                    transition={{
                        duration: 0.5,
                        type: "spring",
                        stiffness: 300,
                        damping: 10,
                        delay: showWrongAnimation ? 0.5 : 3  // Delay based on animation type
                    }}
                >
                    <motion.img
                        src='/images/coin.png'
                        alt="Coin"
                        className='w-24 h-24 object-contain'
                        // Add separate animation for the coin
                        animate={{
                            rotate: [0, -10, 10, -10, 0],
                            scale: [1, 1.1, 1]
                        }}
                        transition={{
                            duration: 0.5,
                            type: "spring",
                            stiffness: 400,
                            damping: 8
                        }}
                    />
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={score}
                            className="text-white text-l font-bold"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{
                                duration: 0.3,
                                delay: showWrongAnimation ? 0.5 : 3  // Match coin animation delay
                            }}
                        >
                            Score: {score}
                        </motion.span>
                    </AnimatePresence>
                </motion.div>

            </div>
            <div className='min-h-screen w-screen overflow-hidden bg-cover bg-center relative' style={{
                backgroundImage: "url('/images/greece_landscape.png')"
            }}>
                {/* <div className="absolute inset-0 flex justify-center items-center space-x-4" style={{ zIndex: 10 }}> */}
                {/* <button
                        className="px-4 py-2 bg-blue-500 bg-opacity-75 text-white rounded hover:bg-blue-600 transition-colors"
                        onClick={() => setIsRunning(!isRunning)} > {/*uses is running state to change definition*/}

                {/* {isRunning ? "Stop Lion Animation" : "Start Lion Animation"} give button another option for is running */}
                {/* </button> */}
                {/* <button
                        className="px-4 py-2 bg-green-500 bg-opacity-75 text-white rounded hover:bg-green-600 transition-colors"
                        onClick={() => setIsFalling(!isFalling)}
                    > */}
                {/* {isFalling ? "Stop Falling Images" : "Start Falling Images"}
                    </button> */}
                {/* </div> */}

                <div className='w-full overflow-hidden'>
                    <RunningLion isRunning={isRunning} />
                    {!isRunning && !showWrongAnimation && fallingImages.map((image) => (
                        <FallingImage
                            key={image.key}
                            src={image.src}
                            column={image.column}
                            spacing={columns.spacing}
                            delay={image.delay}
                            isRunning={isRunning}
                            onClick={() => !image.clicked && handleImageClick(image.key)}
                        />
                    ))}
                </div>

            </div>
            {showLetters && !showWrongAnimation && (
                <GreekTileGrid
                    isFalling={isFalling}
                    setIsFalling={setIsFalling}
                    onLetterClick={handleStartFalling}
                />
            )}
        </>
    )
}

export default GameComponent;